import { MoveButtonsStyled } from './OrderedList.styled';

import { css, Theme } from '@emotion/react';
import styled from '@emotion/styled';
import NavLink from '@OnArk/NavLink';
import navItemState from '@OnArk/styles/navItemState';
import toColor from '@OnArk/utils/toColor';

const NavTitleStyled = styled.span(
  ({ theme }) => css`
    margin: 1.5rem 1rem 0.5rem 1rem;
    overflow: hidden;

    color: ${theme.colors.grayMed};
    font-size: 1rem;
    line-height: 1rem;
    letter-spacing: 1px;
    white-space: nowrap;
    text-transform: uppercase;

    border-bottom: 1px solid transparent;

    transform: all 0.2s;
  `
);

const AdminNavLinkStyled = styled(NavLink)<{
  parent?: string;
  selected?: boolean;
}>(
  ({ theme }) => css`
    position: relative;

    display: block;

    padding: 0.5rem 1.5rem;

    color: ${theme.colors.black};
    font-weight: 500;
    line-height: 1em;
    letter-spacing: 0.5px;

    vertical-align: middle;

    ${navItemState(theme)}
  `
);

export const SectionSidebarItemStyled = styled.a<{
  selected?: never;
  active?: boolean;
  child?: boolean;
}>(
  ({ theme }) => css`
    display: flex;
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
    padding: 0.5rem 0.5rem;

    color: ${theme.colors.white};
    text-decoration: none;

    background: transparent;
    border-radius: 0rem;

    span {
      margin: 0 2rem 0 0;
      overflow: hidden;

      white-space: nowrap;
      text-overflow: ellipsis;
    }

    ${MoveButtonsStyled} {
      top: 0;
      transfrom: none;
    }
    &[data-active='true'],
    &[data-parent='true'] {
      color: ${theme.colors.white};

      background: ${toColor(theme.colors.white, 0.25)};
    }
  `,

  ({ active, theme }) =>
    active &&
    css`
      background: ${theme.colors.white};
    `,
  ({ child, theme }) =>
    child &&
    css`
      i {
        position: relative;
        &:before {
          content: '';

          position: absolute;
          top: -1.1rem;
          bottom: -0.9rem;

          left: -0.25rem;

          border-left: 1px solid ${theme.colors.border};
        }
      }
    `
);

const ChurchDropdownStyled = styled.div<{ open: boolean }>(
  ({ theme }) => ({
    circle: theme.font('lg', 'bl'),
    small: theme.font('xs', 'md'),
  }),
  ({ open, theme }) => css`
    position: relative;
    z-index: 2;

    width: 100%;

    margin: 0;

    font-size: 1.2rem;

    background: ${theme.colors.white};

    figure {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1;

      display: ${open ? 'block' : 'none'};
      margin: 0;
    }
    small {
      width: auto;
      max-width: 24.2rem;
      margin-left: 1.6rem;
      overflow: hidden;

      white-space: nowrap;

      text-overflow: ellipsis;
    }
    a {
      display: grid;
      grid-template-columns: auto 1fr auto;
      justify-content: flex-start;
      align-items: center;
      width: 100%;
      padding: 0 1.5rem;

      font-size: 1.2rem;

      cursor: pointer;
      small {
        flex: 1;

        text-align: left;
      }
    }
    circle {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 2.9rem;
      height: 2.9rem;

      color: ${theme.colors.grayDark};
      font-size: 1.8rem;

      border: 1px solid ${theme.colors.border};
      border-radius: 50%;

      transition: all 0.2s;

      picture {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 4rem;
        overflow: hidden;

        border-radius: 50%;
      }
      span {
        padding: 1rem;
      }
      &:hover {
        box-shadow: ${theme.shadows.sm};
      }
    }

    > nav {
      position: absolute;

      top: 100%;
      right: 0;
      left: 0;

      z-index: 2;

      display: ${open ? 'flex' : 'none'};
      flex-direction: column;
      padding: 1rem 0 0;

      overflow: hidden;

      background: ${theme.colors.white};

      border-bottom-right-radius: 3px;
      box-shadow: 4px 4px 4px rgba(0, 0, 0, 0.1);
      a {
        padding: 1rem 1.5rem;
      }
    }
  `
);

export const OpenSidebarButtonStyled = styled.button(
  ({ theme }) => css`
    position: absolute;
    top: 2rem;
    right: -1rem;
    z-index: 10;

    padding: 0.25rem;

    color: ${theme.colors.white};

    background: ${theme.colors.highlight};
    border-radius: 50%;
    opacity: 0;
  `
);
const condensedSidebarCss = (theme: Theme) => css`
  width: 5.5rem;
  h1 {
    margin: 0 auto;
    > span {
      display: block;
      margin: 0 auto;

      font-size: 0;
      &:first-letter {
        font-size: 2rem;
      }
    }
  }
  ${ChurchDropdownStyled} {
    a {
      i,
      small {
        width: 0;
        overflow: hidden;
      }
    }
  }
  ${NavTitleStyled} {
    height: 0;
    /* The category labels (Help, Account, etc.) are unreadable in
       the 5.5rem rail, so collapse them flush against the surrounding
       links — otherwise they leave inconsistent vertical gaps between
       sections. */
    margin: 0 1rem;
    padding: 0;

    color: ${theme.colors.white};

    border-bottom-color: ${theme.colors.border};
  }
  ${SectionSidebarItemStyled} {
    span {
      margin: 0;
    }
  }
  /* The inner scroll wrapper normally reserves a 1.5rem right gutter
     for an overlay scrollbar, which leaves nav links narrower than
     the rail. In condensed mode drop the gutter so icons center in
     the full 5.5rem rail, keep vertical scrolling so the bottom nav
     items remain reachable, and hide the scrollbar UI so it doesn't
     reclaim layout space and shove icons off-center again. */
  > aside main > * {
    width: 100% !important;
    padding-right: 0 !important;
    overflow-x: hidden !important;
    overflow-y: auto !important;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    /* The grid that holds each nav section has 3rem of row-gap by
       default, which produces inconsistent jumps between sections
       when category labels are hidden. Tighten it on mobile so the
       icons stack with a uniform rhythm. */
    row-gap: 0.4rem !important;
    /* The first nav section uses flex:1 to push the bottom sections
       to the foot of the rail on desktop. On mobile that stretch
       leaves a big empty band between the first and second sections;
       disable it so all icons stack flush from the top. */
    > * {
      flex: 0 0 auto !important;
    }
  }
  ${AdminNavLinkStyled} {
    /* The label is a bare text node next to the icon's wrapper div.
       Shrinking it with font-size:0 still leaves a few pixels of
       advance width that knocks the icon off-center. Position the
       icon's wrapper absolutely instead so the text node falls out
       of the layout flow entirely. */
    position: relative;

    height: 4rem;
    padding: 0;

    font-size: 0;
    /* The wrapper is a div normally, but a span when the icon has a
       tooltip (TooltipStyled renders a span) — admin nav icons always
       have tooltips, so match both. */
    > div,
    > span {
      position: absolute;
      top: 50%;
      left: 50%;

      transform: translate(-50%, -50%);
    }
    i {
      margin-right: 0 !important;
      margin-left: 0 !important;
    }
  }
`;

export const CondesableSidebarStyled = styled.nav<{
  condensed: boolean;
  dark?: boolean;
}>(
  ({ theme }) => css`
    z-index: 3;

    width: 28rem;
    height: 100%;

    letter-spacing: 0.5px;

    background: ${theme.colors.white};

    transition: all 0.2s;
    > aside {
      position: sticky;
      top: 0;

      display: grid;
      grid-template-columns: fit-content;
      grid-template-rows: auto 1fr auto;
      align-items: start;
      width: 100%;

      height: 100vh;
      padding-bottom: 1.5rem;

      main {
        height: 100%;
        overflow: hidden;
        > * {
          width: 100%;
          height: 100%;
          overflow-x: hidden;
          overflow-y: auto;
          scrollbar-width: none;
          &::-webkit-scrollbar {
            display: none;
          }
        }
      }

      a,
      button {
        max-width: 100%;

        white-space: nowrap;
        text-overflow: ellipsis;
      }
    }
    h1 {
      margin: 0;

      padding: 0 1.5rem;
      overflow: hidden;

      font-size: 2rem;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    header {
      display: flex;
      align-items: center;
      height: 6.2rem;

      border-bottom: 1px solid ${theme.colors.border};

      &:hover ${OpenSidebarButtonStyled} {
        opacity: 1;
      }
    }
    footer {
      border-top: 1px solid ${theme.colors.border};
    }
  `,
  ({ condensed, theme }) => condensed && condensedSidebarCss(theme),
  ({ theme }) => css`
    /* Force condensed below the md breakpoint so the sidebar doesn't
       crowd out the editor on phones, regardless of the user's
       persisted desktop preference. */
    @media (max-width: 991px) {
      ${condensedSidebarCss(theme)}
    }
  `,
  ({ dark, theme }) =>
    dark &&
    css`
      color: ${theme.colors.white};

      background-color: ${theme.colors.clickable};
    `
);
